import { Filter, OrderBy } from '@shared/types';
import { useMemo, useState } from 'react';
import { FirestoreDocument } from '../../types';
import { inferFieldsFromDocuments } from '../../utils';
import {
  computeNextSort,
  getFilteredFieldSets,
  isFieldFiltered as isFieldFilteredRule,
} from './document-table.domain';

export interface DocumentTableProps {
  documents: FirestoreDocument[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRowClick: (document: FirestoreDocument) => void;
  sortState: OrderBy | null;
  onSortChange: (sortState: OrderBy | null) => void;
  filters?: Filter[];
  orFilters?: Filter[];
}

export function useDocumentTable({
  documents,
  loading,
  loadingMore = false,
  hasMore,
  onLoadMore,
  onRowClick,
  sortState,
  onSortChange,
  filters = [],
  orFilters = [],
}: DocumentTableProps) {
  const [peekValue, setPeekValue] = useState<{
    value: unknown;
    fieldName: string;
    fieldType: 'array' | 'map';
  } | null>(null);

  const fields = useMemo(() => inferFieldsFromDocuments(documents), [documents]);

  const { andFields, orFields } = useMemo(
    () => getFilteredFieldSets(filters, orFilters),
    [filters, orFilters]
  );

  const isFieldFiltered = (field: string) => isFieldFilteredRule(field, andFields, orFields);

  const handlePeek = (value: unknown, fieldType: 'array' | 'map', fieldName: string) => {
    setPeekValue({ value, fieldName, fieldType });
  };

  const handleSort = (field: string) => {
    onSortChange(computeNextSort(sortState, field));
  };

  const closePeek = () => setPeekValue(null);

  return {
    shouldShowLoadingSpinner: loading && documents.length === 0,
    shouldShowEmptyMessage: !loading && documents.length === 0,
    data: {
      fields,
      documents,
      sortState,
      peekValue,
    },
    isFieldFiltered,
    handleSort,
    handlePeek,
    closePeek,
    hasMore,
    loading,
    loadingMore,
    onLoadMore,
    onRowClick,
  };
}
